import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  alpha,
  useTheme,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import {
  Edit,
  DeleteOutline,
  Group,
  PhotoCamera,
  FamilyRestroom,
  TimerOutlined,
  PriorityHigh,
  AssignmentOutlined,
} from "@mui/icons-material";
import StatusIcon from "./StatusIcon";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const formatTotalMinutes = (totalMinutes) => {
  const safe = Number(totalMinutes) || 0;
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const VisitCallCard = ({ visit, onDelete, onEdit }) => {
  const theme = useTheme();
  const selectedDays = Array.isArray(visit.selected_days_in_week)
    ? visit.selected_days_in_week
    : [];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        background: "#fff",
        position: "relative",
        overflow: "hidden",
        transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 12px 24px ${alpha(theme.palette.common.black, 0.05)}`,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1, pr: 1 }}>
          <Typography
            variant="body1"
            fontWeight={900}
            sx={{ color: "#0f172a", lineHeight: 1.2 }}
          >
            {visit.visit_name || "Visit Name"}
          </Typography>
          {visit.time_critical && (
            <Chip
              icon={<PriorityHigh sx={{ fontSize: "10px !important" }} />}
              label="Time Critical"
              size="small"
              color="error"
              sx={{
                height: 18,
                fontSize: "0.6rem",
                fontWeight: 800,
                mt: 0.5,
              }}
            />
          )}
        </Box>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => onEdit(visit.id, visit.area)}
            sx={{
              color: "info.main",
              bgcolor: alpha(theme.palette.info.main, 0.05),
            }}
          >
            <Edit sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(visit.id)}
            sx={{
              color: "error.main",
              bgcolor: alpha(theme.palette.error.main, 0.05),
            }}
          >
            <DeleteOutline sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </Stack>

      <Box
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <Typography variant="subtitle2" fontWeight={900} color="primary.main">
          {visit?.time_required_from?.slice(0, 5) || "--:--"} -{" "}
          {visit?.time_required_to?.slice(0, 5) || "--:--"}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={900}
          sx={{
            bgcolor: "primary.main",
            color: "#fff",
            px: 1,
            py: 0.2,
            borderRadius: 1,
          }}
        >
          {formatTotalMinutes(visit.total_duration)}
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          fontWeight={800}
          color="text.disabled"
          display="block"
          sx={{ mb: 0.8, fontSize: "0.6rem", textTransform: "uppercase" }}
        >
          Scheduled Days
        </Typography>
        <Stack direction="row" spacing={0.5}>
          {DAYS.map((day) => {
            const isActive = selectedDays.includes(day);
            return (
              <Box
                key={day}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.65rem",
                  fontWeight: 800,
                  bgcolor: isActive ? "primary.main" : "#f1f5f9",
                  color: isActive ? "#fff" : "#94a3b8",
                  border: isActive ? "none" : "1px solid #e2e8f0",
                }}
              >
                {day[0]}
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Box
        sx={{
          bgcolor: "#f8fafc",
          p: 1.5,
          borderRadius: 2.5,
          mb: 2,
          border: "1px solid #f1f5f9",
        }}
      >
        <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
          <AssignmentOutlined sx={{ fontSize: 14, color: "text.disabled" }} />
          <Typography
            variant="caption"
            fontWeight={800}
            color="text.disabled"
            sx={{ fontSize: "0.6rem" }}
          >
            INSTRUCTIONS
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.3,
            fontStyle: visit.support_instructions ? "normal" : "italic",
          }}
        >
          {visit.support_instructions ||
            "No special instructions provided for this visit."}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2, opacity: 0.5 }} />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" spacing={1.5}>
          <StatusIcon
            icon={<PhotoCamera />}
            active={visit.allow_photos}
            color="#f59e0b"
            tooltip="Photos Allowed"
          />
          <StatusIcon
            icon={<FamilyRestroom />}
            active={visit.share_with_family}
            color="#06b6d4"
            tooltip="Shared with Family"
          />
          <Tooltip title={`Tolerance: ${visit.time_tolerance} mins`}>
            <TimerOutlined
              sx={{
                fontSize: 18,
                color: visit.time_tolerance > 0 ? "primary.main" : "#cbd5e1",
              }}
            />
          </Tooltip>
        </Stack>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            bgcolor: "#f1f5f9",
            px: 1,
            py: 0.5,
            borderRadius: 2,
          }}
        >
          <Group sx={{ fontSize: 14, color: "#64748b" }} />
          <Typography variant="caption" fontWeight={900} color="#64748b">
            {visit.carers_required}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

export default VisitCallCard;